import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { useDateFormatter } from './useDateFormatter';

import '../contracts/Caixa_MovimentacaoInput';
import '../contracts/Caixa_MovimentacaoResponse';
import '../contracts/Caixa_ConferirMovimentacaoInput';
import '../contracts/Caixa_ConferirMovimentacaoResponse';

/**
 * Composable para operações do Módulo de Caixa (PDV).
 *
 * Fornece métodos para abertura, fechamento, movimentações
 * e consulta de caixas.
 *
 * TYPED DATA LAYER: Este composable implementa o padrão Typed Data Layer.
 * - DATETIME fields: Usam toApiDateTimeFormat/fromApiDateTimeFormat (ISO 8601 UTC)
 * - Valores monetários: DECIMAL(15,2) do backend, convertidos para number no frontend
 */
export function useCaixa() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const caixaAberto = ref<any | null>(null);
  const extrato = ref<any[]>([]);
  const historico = ref<any[]>([]);
  const saldoAtual = ref<number>(0);

  const {
    toApiDateTimeFormat,
    fromApiDateTimeFormat,
  } = useDateFormatter();

  // Abrir e fechar caixa NÃO existem mais como ações do cliente: o servidor não tem
  // `/caixa/abrir` nem `/caixa/fechar` (o caixa do dia é resolvido por `/caixa/aberto`). As duas
  // funções que os chamavam foram removidas daqui — nenhuma tela as usava.

  // ===========================================
  // MOVIMENTAÇÃO (SANGRIA / SUPRIMENTO)
  // ===========================================

  /**
   * Registra uma movimentação manual no caixa (sangria, suprimento, etc.).
   *
   * @param data Dados da movimentação
   */
  const registrarMovimentacao = async (data: {
    caixa_abertura_id: number;
    tipo: string;
    valor: number;
    entrada_saida: 'E' | 'S';
    cofre_id?: number;
    origem?: string;
    origem_id?: number;
    observacao?: string;
  }) => {
    log('useCaixa', 'registrarMovimentacao', 'Registrando movimentação', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<any>(
        '/index.php/caixa/movimentacao',
        data,
        { inputContract: 'Caixa_MovimentacaoInput', responseContract: 'Caixa_MovimentacaoResponse' }
      );

      if (response.success) {
        await carregarExtrato(data.caixa_abertura_id);
        log('useCaixa', 'registrarMovimentacao', 'Movimentação registrada', response.dados);
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao registrar movimentação');
    } catch (e: any) {
      error.value = e.message;
      log('useCaixa', 'registrarMovimentacao', 'Erro ao registrar movimentação', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================
  // CONSULTA DE CAIXA ABERTO
  // ===========================================

  /**
   * Verifica se existe um caixa aberto para a franquia.
   *
   * @param franquiaId ID da franquia
   * @returns Dados do caixa aberto ou null
   */
  const getCaixaAberto = async (franquiaId: number) => {
    log('useCaixa', 'getCaixaAberto', 'Verificando caixa aberto', { franquiaId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<any>(
        `/index.php/caixa/aberto?franquia_id=${franquiaId}`,
        { responseContract: 'Caixa_AbertoResponse' }
      );

      if (response.success) {
        if (response.dados.tem_caixa_aberto) {
          caixaAberto.value = response.dados;
          saldoAtual.value = response.dados.saldo_atual || 0;
        } else {
          caixaAberto.value = null;
          saldoAtual.value = 0;
        }
        log('useCaixa', 'getCaixaAberto', 'Status do caixa', {
          temCaixaAberto: response.dados.tem_caixa_aberto,
        });
        return response.dados.tem_caixa_aberto ? response.dados : null;
      }
      throw new Error(response.error || 'Erro ao verificar caixa aberto');
    } catch (e: any) {
      error.value = e.message;
      log('useCaixa', 'getCaixaAberto', 'Erro ao verificar caixa aberto', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================
  // EXTRATO
  // ===========================================

  /**
   * Carrega o extrato completo de um caixa.
   *
   * @param caixaId ID do caixa
   */
  const carregarExtrato = async (caixaId: number) => {
    log('useCaixa', 'carregarExtrato', 'Carregando extrato', { caixaId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<any>(
        `/index.php/caixa/extrato/${caixaId}`,
        { responseContract: 'Caixa_ExtratoResponse' }
      );

      if (response.success) {
        extrato.value = response.dados.items || [];
        saldoAtual.value = response.dados.saldo_atual || 0;
        log('useCaixa', 'carregarExtrato', 'Extrato carregado', {
          totalItems: extrato.value.length,
          saldoAtual: saldoAtual.value,
        });
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao carregar extrato');
    } catch (e: any) {
      error.value = e.message;
      log('useCaixa', 'carregarExtrato', 'Erro ao carregar extrato', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================
  // HISTÓRICO DE CAIXAS
  // ===========================================

  /**
   * Lista o histórico de caixas com filtros e paginação.
   *
   * @param filters Filtros opcionais
   */
  const listarCaixas = async (filters?: {
    franquia_id?: number;
    status?: string;
    operador_id?: number;
    data_inicial?: string;
    data_final?: string;
    page?: number;
    per_page?: number;
    /** Flag local (não enviada à API): acumula a página no histórico em vez de substituí-lo. */
    append?: boolean;
  }) => {
    log('useCaixa', 'listarCaixas', 'Listando histórico de caixas', filters);
    isLoading.value = true;
    error.value = null;

    try {
      const queryParams = new URLSearchParams();
      if (filters?.franquia_id) queryParams.set('franquia_id', String(filters.franquia_id));
      if (filters?.status) queryParams.set('status', filters.status);
      if (filters?.operador_id) queryParams.set('operador_id', String(filters.operador_id));
      if (filters?.data_inicial) queryParams.set('data_inicial', filters.data_inicial);
      if (filters?.data_final) queryParams.set('data_final', filters.data_final);
      if (filters?.page) queryParams.set('page', String(filters.page));
      if (filters?.per_page) queryParams.set('per_page', String(filters.per_page));

      const queryString = queryParams.toString();
      const url = '/index.php/caixa' + (queryString ? `?${queryString}` : '');

      const response = await httpService.get<any>(url, {
        responseContract: 'Caixa_IndexResponse',
      });

      if (response.success) {
        const items = response.dados.items || [];
        if (filters?.append && (filters.page || 1) > 1) {
          historico.value = [...historico.value, ...items];
        } else {
          historico.value = items;
        }
        log('useCaixa', 'listarCaixas', 'Histórico carregado', {
          totalItems: historico.value.length,
          pagination: response.dados.pagination,
        });
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao carregar histórico');
    } catch (e: any) {
      error.value = e.message;
      log('useCaixa', 'listarCaixas', 'Erro ao carregar histórico', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================
  // HELPERS
  // ===========================================

  /**
   * Retorna a configuração de badge para cada status de caixa.
   */
  const getStatusConfig = (status: string) => {
    const map: Record<string, { label: string; severity: string; icon: string }> = {
      'aberto': { label: 'Aberto', severity: 'success', icon: 'pi-lock-open' },
      'fechado': { label: 'Fechado', severity: 'info', icon: 'pi-lock' },
      'conferido': { label: 'Conferido', severity: 'success', icon: 'pi-check-circle' },
      'divergente': { label: 'Divergente', severity: 'warning', icon: 'pi-exclamation-triangle' },
    };
    return map[status] || { label: status, severity: 'secondary', icon: 'pi-question-circle' };
  };

  /**
   * Retorna a configuração de badge para cada tipo de movimentação.
   */
  const getTipoMovimentacaoConfig = (tipo: string, entradaSaida: string) => {
    const map: Record<string, { label: string; severity: string }> = {
      'abertura': { label: 'Abertura', severity: 'info' },
      'venda': { label: 'Venda', severity: 'success' },
      'suprimento': { label: 'Suprimento', severity: 'success' },
      'sangria': { label: 'Sangria', severity: 'warning' },
      'ajuste_positivo': { label: 'Ajuste (+)', severity: 'info' },
      'ajuste_negativo': { label: 'Ajuste (−)', severity: 'warning' },
      'despesa_dinheiro': { label: 'Despesa', severity: 'danger' },
      'recebimento_avulso': { label: 'Receb. Avulso', severity: 'success' },
      'pagamento_despesa': { label: 'Pagamento', severity: 'danger' },
      'estorno': { label: 'Estorno', severity: 'danger' },
    };
    return map[tipo] || {
      label: tipo,
      severity: entradaSaida === 'E' ? 'success' : 'danger',
    };
  };

  // ===========================================
  // CONFERÊNCIA DE MOVIMENTAÇÃO
  // ===========================================

  /**
   * Alterna o status de conferência de uma movimentação.
   */
  const conferirMovimentacao = async (id: number, conferido: boolean) => {
    log('useCaixa', 'conferirMovimentacao', 'Alternando conferência', { id, conferido });
    try {
      await httpService.post<any>(
        `/index.php/caixa/movimentacao/conferir/${id}`,
        { conferido },
        { inputContract: 'Caixa_ConferirMovimentacaoInput', responseContract: 'Caixa_ConferirMovimentacaoResponse' },
      );
      // Atualiza o item localmente
      const item = extrato.value.find((m: any) => m.id === id);
      if (item) item.conferido = conferido ? 1 : 0;
      log('useCaixa', 'conferirMovimentacao', 'Conferência atualizada');
    } catch (e: any) {
      log('useCaixa', 'conferirMovimentacao', 'Erro ao conferir', e);
      throw e;
    }
  };

  return {
    // Estado
    isLoading,
    error,
    caixaAberto,
    extrato,
    historico,
    saldoAtual,

    // Ações
    registrarMovimentacao,
    getCaixaAberto,
    carregarExtrato,
    listarCaixas,
    conferirMovimentacao,

    // Helpers
    getStatusConfig,
    getTipoMovimentacaoConfig,
  };
}
